/**
 * useBackgroundSync Hook
 *
 * React hook for integrating background sync functionality
 * Provides sync status, queue management, and user notifications
 */

import { useState, useEffect, useCallback } from 'react';
import { backgroundSync } from '../services/backgroundSync';

interface BackgroundSyncState {
  isOnline: boolean;
  isSyncing: boolean;
  queueCount: number;
  lastSyncTime: number | null;
  lastSyncSuccess: number;
  lastSyncFailure: number;
}

export function useBackgroundSync() {
  const [state, setState] = useState<BackgroundSyncState>({
    isOnline: navigator.onLine,
    isSyncing: false,
    queueCount: 0,
    lastSyncTime: null,
    lastSyncSuccess: 0,
    lastSyncFailure: 0,
  });

  // Update online status
  useEffect(() => {
    const handleOnline = () => {
      setState(prev => ({ ...prev, isOnline: true }));
    };

    const handleOffline = () => {
      setState(prev => ({ ...prev, isOnline: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Update queue count periodically
  useEffect(() => {
    const updateQueueCount = async () => {
      const count = await backgroundSync.getQueueCount();
      setState(prev => ({ ...prev, queueCount: count }));
    };

    updateQueueCount();
    const interval = setInterval(updateQueueCount, 5000);

    return () => clearInterval(interval);
  }, []);

  // Manually trigger sync
  const sync = useCallback(async () => {
    setState(prev => ({ ...prev, isSyncing: true }));

    try {
      await backgroundSync.sync();

      const count = await backgroundSync.getQueueCount();
      setState(prev => ({
        ...prev,
        isSyncing: false,
        queueCount: count,
        lastSyncTime: Date.now(),
      }));
    } catch (error) {
      console.error('[useBackgroundSync] Sync failed:', error);
      setState(prev => ({ ...prev, isSyncing: false }));
    }
  }, []);

  // Queue a request
  const queueRequest = useCallback(
    async (url: string, method: string, options?: RequestInit) => {
      await backgroundSync.queueRequest(url, method, options);
      const count = await backgroundSync.getQueueCount();
      setState(prev => ({ ...prev, queueCount: count }));
    },
    []
  );

  // Clear the queue
  const clearQueue = useCallback(async () => {
    await backgroundSync.clearQueue();
    setState(prev => ({ ...prev, queueCount: 0 }));
  }, []);

  return {
    ...state,
    sync,
    queueRequest,
    clearQueue,
  };
}
